import React, { useEffect, useState, useRef } from 'react';
import { useParams } from 'wouter';
import { useQuery } from '@tanstack/react-query';
import html2canvas from 'html2canvas';

// Helper to format date
const formatMonth = (monthStr: string) => {
    try {
        const date = new Date(monthStr + "-01");
        // French locale hardcoded as requested context implies FR primary
        return date.toLocaleString('fr-FR', { month: 'long' }).toUpperCase();
    } catch {
        return monthStr;
    }
};

// ELO Tier Logic (Duplicated from backend for now to avoid API changes)
const ELO_TIERS = [
    { min: 0, max: 1149, name: { fr: "Débutant", en: "Beginner" }, level: 1 },
    { min: 1150, max: 1299, name: { fr: "Perfectionnement", en: "Improvement" }, level: 2 },
    { min: 1300, max: 1399, name: { fr: "Élémentaire", en: "Elementary" }, level: 3 },
    { min: 1400, max: 1499, name: { fr: "Intermédiaire", en: "Intermediate" }, level: 4 },
    { min: 1500, max: 1599, name: { fr: "Confirmé", en: "Confirmed" }, level: 5 },
    { min: 1600, max: 1749, name: { fr: "Avancé", en: "Advanced" }, level: 6 },
    { min: 1750, max: 1999, name: { fr: "Expert", en: "Expert" }, level: 7 },
    { min: 2000, max: Infinity, name: { fr: "Élite", en: "Elite" }, level: 8 }
];

export default function MonthlyRecapPage() {
    const { playerId, month } = useParams();
    const recapRef = useRef<HTMLDivElement>(null);
    const [isSharing, setIsSharing] = useState(false);

    const { data: stats, isLoading, error } = useQuery({
        queryKey: ['monthlyStats', playerId, month],
        queryFn: async () => {
            const res = await fetch(`/api/players/${playerId}/monthly-stats?month=${month}`);
            if (!res.ok) throw new Error('Failed to fetch stats');
            return res.json();
        }
    });

    const handleShare = async () => {
        if (!recapRef.current || !stats) return;
        setIsSharing(true);

        try {
            // Generate canvas
            const canvas = await html2canvas(recapRef.current, {
                useCORS: true, // For external images like avatars if any
                scale: 1, // Exact dimensions (container is already 1080x1920)
                backgroundColor: '#0a192f', // Match background
                width: 1080,
                height: 1920,
                onclone: (clonedDoc) => {
                    // Reset transform in the cloned document so it captures full size
                    const container = clonedDoc.getElementById('social-share-capture');
                    if (container) {
                        container.style.transform = 'none';
                    }
                }
            });

            // Convert to blob
            canvas.toBlob(async (blob) => {
                if (!blob) {
                    setIsSharing(false);
                    return;
                }

                // Check for Web Share API support with files
                const nav = navigator as any;
                if (nav.share && nav.canShare && nav.canShare({ files: [new File([blob], 'recap.png', { type: 'image/png' })] })) {
                    try {
                        const file = new File([blob], `bilan-${month}.png`, { type: 'image/png' });
                        await nav.share({
                            title: `Mon Bilan PadELO - ${formatMonth(month || '')}`,
                            text: `Découvrez mes stats PadELO pour ${formatMonth(month || '')} !`,
                            files: [file]
                        });
                    } catch (err) {
                        console.error('Error sharing:', err);
                    }
                } else {
                    // Fallback: Download image
                    const link = document.createElement('a');
                    link.download = `bilan-${month}.png`;
                    link.href = canvas.toDataURL('image/png');
                    link.click();
                }
                setIsSharing(false);
            }, 'image/png');

        } catch (err) {
            console.error('Error generating image:', err);
            setIsSharing(false);
        }
    };

    if (isLoading) return <div className="min-h-screen bg-slate-900 flex items-center justify-center text-white">Chargement...</div>;
    if (error) return <div className="min-h-screen bg-slate-900 flex items-center justify-center text-red-400">Erreur lors du chargement des statistiques.</div>;
    if (!stats) return null;

    const currentTier = ELO_TIERS.find(tier => stats.elo >= tier.min && stats.elo <= tier.max) || ELO_TIERS[0];
    const levelDisplay = `${currentTier.name.fr} (${currentTier.level})`;

    // Use same colors/styles as email for consistency, but with Tailwind
    return (
        <div className="min-h-screen bg-slate-900 flex flex-col items-center justify-center p-4">
            {/* Action Bar */}
            <div className="w-full max-w-md mb-6 flex justify-between items-center">
                <button
                    onClick={() => window.location.href = '/'}
                    className="text-slate-400 hover:text-white transition-colors"
                >
                    ← Retour
                </button>
                <button
                    onClick={handleShare}
                    disabled={isSharing}
                    className="bg-amber-400 hover:bg-amber-500 text-black font-bold py-2 px-6 rounded-full transition-colors flex items-center gap-2 shadow-lg shadow-amber-900/20"
                >
                    {isSharing ? 'Génération...' : 'Partager'}
                    {!isSharing && (
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="18" cy="5" r="3" /><circle cx="6" cy="12" r="3" /><circle cx="18" cy="19" r="3" /><line x1="8.59" y1="13.51" x2="15.42" y2="17.49" /><line x1="15.41" y1="6.51" x2="8.59" y2="10.49" /></svg>
                    )}
                </button>
            </div>

            {/* Main Preview Container - Scaled down to fit screen */}
            <div className="relative w-full max-w-[400px] h-[711px] overflow-hidden rounded-xl shadow-2xl border border-slate-700">
                {/* The Recap Card (Exactly 1080x1920 for Story format) */}
                <div
                    ref={recapRef}
                    id="social-share-capture"
                    className="origin-top-left bg-[#0a192f] text-white overflow-hidden relative flex flex-col items-center"
                    style={{
                        width: '1080px',
                        height: '1920px',
                        transform: 'scale(0.37037)', // 400 / 1080 = ~0.37
                        fontFamily: 'Arial, sans-serif'
                    }}
                >
                    {/* Header Image Background */}
                    <div className="relative w-full flex flex-col items-center justify-center text-center overflow-visible mt-20">
                        {/* Banner Image */}
                        <div className="w-full h-[240px] relative flex justify-center items-center mb-8">
                            <img
                                src="/email-assets/bg_card_header.png"
                                alt="Header Background"
                                className="h-full w-auto object-contain scale-150"
                            />
                        </div>

                        {/* Title */}
                        <div className="relative z-10 w-full px-12 mt-4">
                            <h1 className="text-[52px] font-black uppercase tracking-[4px] text-white drop-shadow-lg w-full whitespace-nowrap overflow-hidden text-ellipsis">
                                BILAN DU MOIS - <span className="text-[#fbbf24]">{formatMonth(stats.month)}</span>
                            </h1>
                        </div>
                    </div>

                    {/* Content Container */}
                    <div className="w-full px-12 pt-8 flex-1 flex flex-col gap-12">

                        {/* 1. Player Stats */}
                        {/* 1. Player Stats */}
                        <div className="relative mt-8">
                            <div className="absolute -top-7 left-0 w-full flex justify-center z-10">
                                <div className="bg-[#0a192f] px-6 text-[#fbbf24] font-bold text-[28px] uppercase tracking-wider flex items-center justify-center min-h-[48px]">
                                    <span style={{ paddingTop: '4px' }}>CLASSEMENT</span>
                                </div>
                            </div>
                            <div className="border-4 border-[#fbbf24] rounded-[20px] bg-[#1e293b]/80 p-0 m-0 pt-12 pb-8">
                                <div className="flex items-center w-full px-2">
                                    {/* Icon */}
                                    <div className="w-[25%] flex justify-center shrink-0">
                                        <img src="/email-assets/icon_globe_gold.png" alt="Rank" className="w-[104px] h-[104px] object-contain block" />
                                    </div>

                                    {/* Details */}
                                    <div className="w-[45%] flex flex-col justify-center pl-2">
                                        <div className="text-[40px] font-bold text-white mb-2 leading-normal whitespace-nowrap pt-1 pb-2">
                                            {((name) => {
                                                const cleanName = name.trim();
                                                const parts = cleanName.split(' ');
                                                if (cleanName.length > 15 && parts.length > 1) { // 15 chars max to fit
                                                    return `${parts[0]} ${parts[parts.length - 1][0]}.`;
                                                }
                                                return cleanName;
                                            })(stats.playerName)}
                                        </div>
                                        <div className="text-[28px] font-black text-white leading-normal whitespace-nowrap pb-2">
                                            ELO: <span className="text-[#fbbf24]">{stats.elo}</span>
                                            <span className={`ml-2 text-[24px] ${stats.eloChange >= 0 ? 'text-[#22c55e]' : 'text-[#ef4444]'}`}>
                                                ({stats.eloChange >= 0 ? '+' : ''}{stats.eloChange})
                                            </span>
                                        </div>
                                        <div className="text-[28px] font-black text-white leading-normal whitespace-nowrap pb-1">
                                            NIVEAU: <span className="text-[#fbbf24]">{levelDisplay}</span>
                                        </div>
                                    </div>

                                    {/* Position */}
                                    <div className="w-[30%] flex flex-col items-center justify-center">
                                        <div className="text-[52px] font-black text-white leading-none">
                                            #<span className="text-[#fbbf24]">{stats.globalRank}{stats.globalRank === 1 ? 'er' : 'e'}</span>
                                        </div>
                                        <div className={`text-[36px] font-black mt-2 ${stats.globalRankChange > 0 ? 'text-[#22c55e]' : (stats.globalRankChange < 0 ? 'text-[#ef4444]' : 'text-[#94a3b8]')}`}>
                                            ({stats.globalRankChange > 0 ? '+' : ''}{stats.globalRankChange === 0 ? '=' : stats.globalRankChange})
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* 2. Summary Stats */}
                        <div className="relative mt-4">
                            <div className="absolute -top-7 left-0 w-full flex justify-center z-10">
                                <div className="bg-[#0a192f] px-6 text-[#fbbf24] font-bold text-[28px] uppercase tracking-wider flex items-center justify-center min-h-[48px]">
                                    <span style={{ paddingTop: '4px' }}>RÉSULTATS</span>
                                </div>
                            </div>
                            <div className="border-4 border-[#fbbf24] rounded-[20px] bg-[#1e293b]/80 p-0 m-0 pt-10 pb-6">
                                <div className="px-0 flex justify-center w-full gap-0">
                                    {/* Matches */}
                                    <div className="w-1/3 flex flex-col items-center">
                                        <img src="/email-assets/icon_match_gold.png" className="w-[96px] h-[96px] mb-2 block" />
                                        <div className="text-[48px] font-black text-white leading-none">{stats.matches}</div>
                                        <div className="text-[18px] text-[#94a3b8] font-bold uppercase whitespace-nowrap mt-1">MATCHS</div>
                                    </div>
                                    {/* Wins */}
                                    <div className="w-1/3 flex flex-col items-center">
                                        <img src="/email-assets/icon_win_gold.png" className="w-[96px] h-[96px] mb-2 block" />
                                        <div className="text-[48px] font-black text-white leading-none">{stats.wins}</div>
                                        <div className="text-[18px] text-[#94a3b8] font-bold uppercase whitespace-nowrap mt-1">VICTOIRES</div>
                                    </div>
                                    {/* WinRate */}
                                    <div className="w-1/3 flex flex-col items-center">
                                        <img src="/email-assets/icon_trophy_gold.png" className="w-[96px] h-[96px] mb-2 block" />
                                        <div className="text-[48px] font-black text-white leading-none">{Math.round(stats.winRate)}%</div>
                                        <div className="text-[18px] text-[#94a3b8] font-bold uppercase whitespace-nowrap mt-1">PERCENT.</div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* 3. Top Partners */}
                        {stats.topPartners && stats.topPartners.length > 0 && (
                            <div className="relative mt-4">
                                <div className="absolute -top-7 left-0 w-full flex justify-center z-10">
                                    <div className="bg-[#0a192f] px-6 text-[#fbbf24] font-bold text-[28px] uppercase tracking-wider flex items-center justify-center min-h-[48px]">
                                        <span style={{ paddingTop: '4px' }}>PARTENAIRES</span>
                                    </div>
                                </div>
                                <div className="border-4 border-[#fbbf24] rounded-[20px] bg-[#1e293b]/80 p-0 m-0 pt-10 pb-6">
                                    <div className="p-0">
                                        <table className="w-full border-collapse table-fixed">
                                            <tbody>
                                                {stats.topPartners.map((partner: any, i: number) => (
                                                    <tr key={i} className="border-b-2 border-white/10 last:border-0">
                                                        <td className="p-3 pl-6 w-1/3 text-[26px] font-bold text-white align-middle whitespace-nowrap overflow-hidden text-ellipsis text-left">
                                                            <span className="text-[#fbbf24] mr-2">#{i + 1}</span>
                                                            {((name) => {
                                                                const parts = name.split(' ');
                                                                if (parts.length > 1) {
                                                                    return `${parts[0]} ${parts[parts.length - 1][0]}.`;
                                                                }
                                                                return name;
                                                            })(partner.partnerName)}
                                                        </td>
                                                        <td className="p-3 w-1/3 text-center text-[26px] font-bold align-middle text-white whitespace-nowrap">
                                                            {partner.winRate}%
                                                        </td>
                                                        <td className="p-3 pr-6 w-1/3 text-center text-[22px] text-[#94a3b8] align-middle whitespace-nowrap">
                                                            {partner.matchesPlayed} M
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                        {(stats.totalPartners > 3 || (stats.topPartners.length === 3 && stats.totalPartners > 3)) && (
                                            <div className="text-center p-3 pt-2">
                                                <div className="text-[20px] text-[#64748b] italic whitespace-nowrap">
                                                    + {stats.totalPartners - 3} autres partenaires
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* 4. Clubs */}
                        {stats.clubs && (
                            <div className="relative mt-4">
                                <div className="absolute -top-7 left-0 w-full flex justify-center z-10">
                                    <div className="bg-[#0a192f] px-6 text-[#fbbf24] font-bold text-[28px] uppercase tracking-wider flex items-center justify-center min-h-[48px]">
                                        <span style={{ paddingTop: '4px' }}>CLUBS</span>
                                    </div>
                                </div>
                                <div className="border-4 border-[#fbbf24] rounded-[20px] bg-[#1e293b]/80 p-0 m-0 pt-10 pb-6">
                                    <div className="p-0">
                                        <table className="w-full border-collapse table-fixed">
                                            <tbody>
                                                {stats.clubs.slice(0, 3).map((club: any, i: number) => (
                                                    <tr key={i} className="border-b-2 border-white/10 last:border-0">
                                                        <td className="p-3 pl-6 w-[35%] text-[26px] font-bold text-white align-middle whitespace-nowrap overflow-hidden text-ellipsis text-left">
                                                            <span className="text-[#fbbf24] mr-2">#{i + 1}</span> {club.name}
                                                        </td>
                                                        <td className="p-3 w-[25%] text-center text-[26px] font-bold text-white align-middle whitespace-nowrap">
                                                            <div className="flex items-center justify-center gap-2">
                                                                <span>{club.rank}{club.rank === 1 ? 'er' : 'e'}</span>
                                                                <span className={`text-[26px] ${club.rankChange > 0 ? 'text-[#22c55e]' : (club.rankChange < 0 ? 'text-[#ef4444]' : 'text-[#94a3b8]')}`}>
                                                                    ({club.rankChange > 0 ? '+' : ''}{club.rankChange === 0 ? '=' : club.rankChange})
                                                                </span>
                                                            </div>
                                                        </td>
                                                        <td className="p-3 w-[20%] text-center text-[26px] font-bold align-middle whitespace-nowrap">
                                                            <span className={`${club.eloChange >= 0 ? 'text-[#22c55e]' : 'text-[#ef4444]'}`}>
                                                                {club.eloChange >= 0 ? '+' : ''}{club.eloChange} ELO
                                                            </span>
                                                        </td>
                                                        <td className="p-3 pr-6 w-[20%] text-right text-[22px] text-[#94a3b8] align-middle whitespace-nowrap">
                                                            {club.matches} M
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                        {(stats.totalClubs > 3 || (stats.clubs.length > 3)) && (
                                            <div className="text-center p-3 pt-2">
                                                <div className="text-[20px] text-[#64748b] italic whitespace-nowrap">
                                                    + {(stats.totalClubs || stats.clubs.length) - 3} autres groupes
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* 5. Footer inside Image */}
                    <div className="flex flex-col items-center justify-center mt-auto mb-16 w-full">
                        <div className="text-center w-full">
                            <div className="text-[#fbbf24] font-black text-[40px] uppercase tracking-widest drop-shadow-md">www.padelo.fr</div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="mt-4 text-slate-500 text-xs text-center max-w-sm">
                Astuce: Si le partage ne fonctionne pas, faites une capture d'écran !
            </div>
        </div>
    );
}
